import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { TrendingUp, Eye, Clock, Search, ArrowRight, Star, Users, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";

const metrics = [
  { label: "Temps passé sur le site", before: "8s", after: "2min 34s", icon: Clock, improvement: "+1 800%" },
  { label: "Taux de contact", before: "0.5%", after: "8.2%", icon: Users, improvement: "+1 540%" },
  { label: "Position Google", before: "Non indexé", after: "Top 3", icon: Search, improvement: "#1 page" },
  { label: "Nouveaux clients/mois", before: "0–1", after: "6–8", icon: TrendingUp, improvement: "+600%" },
];

function AnimatedBar({ value, delay, color }: { value: number; delay: number; color: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true });
  return (
    <div ref={ref} className="h-2 rounded-full bg-muted/30 overflow-hidden">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={inView ? { width: `${value}%` } : {}}
        transition={{ delay, duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

const SellCaseStudy = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section className="py-24 relative overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            📊 Étude de cas réelle
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            De l'invisible au{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              n°1 sur Google
            </span>
          </h2>
          <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto">
            Comment Sophie M., photographe à Toulouse, a multiplié par 6 ses clients grâce à son portfolio Folio Studio.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Client profile card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 p-6 rounded-2xl bg-card/60 border border-border/50 backdrop-blur-sm flex flex-col sm:flex-row items-center gap-6"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg shadow-primary/20">
              SM
            </div>
            <div className="text-center sm:text-left flex-1">
              <h3 className="font-bold text-lg">Sophie M.</h3>
              <p className="text-muted-foreground text-sm">Photographe professionnelle · Toulouse</p>
              <p className="text-muted-foreground text-xs mt-1">Formule « Clé en main » — Livré en 36h</p>
            </div>
            <div className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Star className="w-4 h-4 fill-primary text-primary" />
              <span className="text-sm font-bold">5/5</span>
            </div>
          </motion.div>

          {/* Timeline */}
          <div className="relative mb-12">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border/50 hidden md:block" />
            
            {[
              { day: "J+0", title: "Brief & contenu reçu", desc: "Sophie envoie ses photos, textes et préférences de style via notre formulaire." },
              { day: "J+1", title: "Première version livrée", desc: "Portfolio complet avec dark mode, galerie responsive et SEO configuré." },
              { day: "J+2", title: "Révisions & mise en ligne", desc: "2 ajustements mineurs. Domaine personnalisé connecté. Portfolio live." },
              { day: "J+30", title: "Résultats mesurés", desc: "6 nouveaux clients, 1ère page Google sur 'photographe Toulouse'. ROI immédiat." },
            ].map((step, i) => (
              <motion.div
                key={step.day}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="flex gap-4 md:gap-6 mb-6 last:mb-0"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-xs z-10">
                  {step.day}
                </div>
                <div className="flex-1 p-4 rounded-xl bg-card/40 border border-border/30">
                  <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
                  <p className="text-muted-foreground text-xs">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Metrics grid */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {metrics.map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-5 rounded-2xl bg-card/60 border border-border/50 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <m.icon className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium text-muted-foreground">{m.label}</span>
                  </div>
                  <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    {m.improvement}
                  </span>
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Avant</span>
                      <span className="font-mono text-destructive">{m.before}</span>
                    </div>
                    <AnimatedBar value={15} delay={0.3 + i * 0.1} color="bg-destructive/40" />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Après</span>
                      <span className="font-mono text-primary font-bold">{m.after}</span>
                    </div>
                    <AnimatedBar value={90} delay={0.5 + i * 0.1} color="bg-gradient-to-r from-primary to-accent" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quote */}
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 mb-8"
          >
            <p className="text-base sm:text-lg md:text-xl italic text-foreground/90 mb-4 max-w-2xl mx-auto">
              "Le meilleur investissement pour mon activité. En un mois, j'ai récupéré 10x le prix du portfolio en nouveaux contrats."
            </p>
            <cite className="text-sm text-muted-foreground not-italic">— Sophie M., Photographe</cite>
          </motion.blockquote>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <a href="#pricing">
              <Button size="lg" className="text-base px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group">
                Je veux les mêmes résultats
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SellCaseStudy;
